import React from 'react';
import { Link } from 'react-router-dom';
import { Game, Team } from '../types';
import { parseGameTime } from '../utils/gameTime';
import { mediaUrl } from '../utils/mediaUrl';

interface GameCardProps {
  game:  Game;
  teams: Team[];
}

const SPORT_ICONS: Record<string, string> = {
  baseball:   '⚾',
  softball:   '🥎',
  soccer:     '⚽',
  basketball: '🏀',
};

const fmtDate = (s: string) =>
  parseGameTime(s).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

const fmtTime = (s: string) =>
  parseGameTime(s).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

/** Small colored square representing a team */
const TeamMark = ({ team, abbr }: { team?: Team; abbr: string }) => {
  const bg   = team?.primary_color || '#0F2748';
  const logo = team?.logo_url;
  return (
    <div className="gc-team-mark" style={{ background: bg }} title={team?.name}>
      {logo
        ? <img src={mediaUrl(logo)} alt={abbr} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : abbr}
    </div>
  );
};

const GameCard: React.FC<GameCardProps> = ({ game, teams }) => {
  const homeTeam = teams.find(t => t.id === game.team_id);

  const homeAbbr = (game.home_team || '?').slice(0, 3).toUpperCase();
  const awayAbbr = (game.away_team || '?').slice(0, 3).toUpperCase();

  const isLive      = game.status === 'live';
  const isCompleted = game.status === 'completed';
  const hasScore    = game.home_final_score != null && game.away_final_score != null;

  const homeScore = hasScore ? game.home_final_score : null;
  const awayScore = hasScore ? game.away_final_score : null;
  const homeWon   = hasScore && homeScore! > awayScore!;
  const awayWon   = hasScore && awayScore! > homeScore!;
  const tied      = hasScore && !homeWon && !awayWon;

  return (
    <div className={`gc${isLive ? ' live' : ''}`}>
      {/* Meta row */}
      <div className="gc-meta">
        {isLive && (
          <span className="gc-live-pill">
            <span className="dot" />LIVE
          </span>
        )}
        <span>{SPORT_ICONS[game.sport] || '🏅'} {game.sport}</span>
        {isCompleted && <span style={{ marginLeft: 'auto', color: 'var(--ink-4)' }}>Final</span>}
        {!isLive && !isCompleted && <span style={{ marginLeft: 'auto' }}>{fmtTime(game.scheduled_at)}</span>}
      </div>

      {/* Teams */}
      <div className="gc-teams">
        {/* Away */}
        <div className="gc-team-row">
          <div className="gc-team-mark" style={{ background: '#94A0B0' }}>{awayAbbr}</div>
          <span className={`gc-team-name${isCompleted && homeWon ? ' dim' : ''}`}>{game.away_team}</span>
          {hasScore && <span className={`gc-score${homeWon ? ' dim' : ''}`}>{awayScore}</span>}
          {isCompleted && (
            awayWon ? <span className="gc-outcome W">W</span>
            : homeWon ? <span className="gc-outcome L">L</span>
            : <span className="gc-outcome T">T</span>
          )}
        </div>
        {/* Home */}
        <div className="gc-team-row">
          <TeamMark team={homeTeam} abbr={homeAbbr} />
          <span className={`gc-team-name${isCompleted && awayWon ? ' dim' : ''}`}>{game.home_team}</span>
          {hasScore && <span className={`gc-score${awayWon ? ' dim' : ''}`}>{homeScore}</span>}
          {isCompleted && (
            homeWon ? <span className="gc-outcome W">W</span>
            : awayWon ? <span className="gc-outcome L">L</span>
            : <span className="gc-outcome T">T</span>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="gc-footer">
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {game.location ? `📍 ${game.location}` : fmtDate(game.scheduled_at)}
        </span>
        {isLive ? (
          <Link
            to={`/watch/${game.id}`}
            style={{ fontWeight: 700, fontSize: '13px', color: 'var(--live)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--live)', display: 'inline-block' }} />
            Watch Live
          </Link>
        ) : isCompleted ? (
          <Link to={`/watch/${game.id}`} style={{ fontWeight: 600, fontSize: '13px', color: 'var(--sr-teal)', textDecoration: 'none', flexShrink: 0 }}>
            Replay →
          </Link>
        ) : (
          <span style={{ fontWeight: 600, color: 'var(--team-primary)', fontSize: '13px', flexShrink: 0 }}>Upcoming</span>
        )}
      </div>
    </div>
  );
};

export default GameCard;
